import React from "react";
import { motion } from "framer-motion";
import { TrendingDown } from "lucide-react";

const comparisons = [
  {
    label: "Small practice",
    sub: "< $120K / mo collections",
    integrate: {
      total: "$1,200 + 3.5%",
      breakdown: "$800 AI + $400 integration fee + 3.5% of collections",
    },
    native: {
      total: "$1,200",
      breakdown: "flat AI cost — no percentage at this tier",
    },
    savingsNote: "3.5% of collections saved",
    example: "At $80K/mo collections → save ~$2,800/mo",
  },
  {
    label: "Growing practice",
    sub: "$120K – $200K / mo",
    integrate: {
      total: "$1,200 + 3.5%",
      breakdown: "$800 AI + $400 integration fee + 3.5% of collections",
    },
    native: {
      total: "2%",
      breakdown: "of collections — no flat fee",
    },
    savingsNote: "1.5% of collections saved",
    example: "At $150K/mo collections → save ~$3,450/mo",
  },
  {
    label: "Enterprise practice",
    sub: "$200K+ / mo",
    integrate: {
      total: "$1,200 + 3.5%",
      breakdown: "$800 AI + $400 integration fee + 3.5% of collections",
    },
    native: {
      total: "1.65%",
      breakdown: "of collections — volume rate",
    },
    savingsNote: "1.85% of collections saved",
    example: "At $250K/mo collections → save ~$5,825/mo",
  },
];

const PMSEfficiencyCaseSlide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex flex-col items-center justify-center p-8 xl:p-12 relative overflow-hidden font-sans">
      {/* Ambient blue gradient blurs */}
      <div
        className="absolute top-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-30 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #bfdbfe 0%, #93c5fd 50%, #dbeafe 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #bfdbfe 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full flex flex-col items-center relative z-10 max-w-[1180px]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h2 className="text-3xl lg:text-[42px] font-bold text-slate-900 tracking-tight leading-tight">
            AI is dramatically more efficient on{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Asha's AI-Native PMS.
            </span>
          </h2>
          <p className="text-slate-900 text-[22px] font-semibold mt-6 max-w-3xl mx-auto leading-relaxed">
            When Asha's AI runs on our native PMS, it doesn't have to do excessive reads and writes
            across a foreign system. That efficiency gets passed back to you — your AI cost drops
            as you scale.
          </p>
        </motion.div>

        {/* Comparison table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="w-full rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-[0_8px_60px_-12px_rgba(0,0,0,0.08)]"
        >
          <div className="grid grid-cols-[1fr_1.35fr_1.1fr_1.15fr] bg-slate-50 border-b border-slate-200">
            <div className="px-6 py-4 text-[14px] font-bold text-slate-700 uppercase tracking-wider">
              Practice size
            </div>
            <div className="px-6 py-4 text-[14px] font-bold text-slate-700 uppercase tracking-wider border-l border-slate-200">
              AI cost in integrated model
            </div>
            <div className="px-6 py-4 text-[14px] font-bold text-blue-600 uppercase tracking-wider border-l border-slate-200">
              AI cost in Asha PMS model
            </div>
            <div className="px-6 py-4 text-[14px] font-bold text-emerald-600 uppercase tracking-wider border-l border-slate-200">
              You save
            </div>
          </div>

          {comparisons.map((row, i) => (
            <div
              key={i}
              className="grid grid-cols-[1fr_1.35fr_1.1fr_1.15fr] border-b border-slate-100 last:border-b-0 hover:bg-slate-50/40 transition-colors"
            >
              <div className="px-6 py-6 flex flex-col justify-center">
                <p className="text-[18px] font-bold text-slate-900">{row.label}</p>
                <p className="text-[14px] text-slate-600 mt-1 font-medium">{row.sub}</p>
              </div>
              <div className="px-6 py-6 border-l border-slate-100 flex flex-col justify-center">
                <p className="text-[22px] font-extrabold text-slate-900 tracking-tight">
                  {row.integrate.total}
                </p>
                <p className="text-[14px] text-slate-700 mt-1.5 leading-snug">
                  {row.integrate.breakdown}
                </p>
              </div>
              <div className="px-6 py-6 border-l border-slate-100 flex flex-col justify-center">
                <p className="text-[22px] font-extrabold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent tracking-tight">
                  {row.native.total}
                </p>
                <p className="text-[14px] text-slate-700 mt-1.5 leading-snug">
                  {row.native.breakdown}
                </p>
              </div>
              <div className="px-6 py-6 border-l border-slate-100 flex flex-col justify-center">
                <div className="inline-flex items-center gap-1.5 text-emerald-600 mb-1.5">
                  <TrendingDown className="w-4 h-4" />
                  <p className="text-[15px] font-bold">{row.savingsNote}</p>
                </div>
                <p className="text-[14px] text-slate-700 leading-snug">{row.example}</p>
              </div>
            </div>
          ))}
        </motion.div>

        <p className="mt-6 text-[17px] font-medium text-slate-800 max-w-3xl text-center leading-relaxed">
          Native AI avoids redundant API calls into a third-party PMS — every patient lookup,
          schedule read, and claim write is one hop instead of three. That's why we can pass the
          savings back to you as you scale.
        </p>
      </div>
    </div>
  );
};

export default PMSEfficiencyCaseSlide;
